import React, { PropsWithChildren, useContext } from "react";
import AccessControlApis from "../shared/apiHandlers/AccessControlAPIs";
import AccountsApis from "../shared/apiHandlers/AccountsAPIs";
import AddressAPIs from "../shared/apiHandlers/AddressAPIs";
import AssetAPIs from "../shared/apiHandlers/AssetAPIs";
import AssignmentAPIs from "../shared/apiHandlers/AssignmentAPIs";
import CompanyAPIs from "../shared/apiHandlers/CompanyAPIs";
import CustomerAPIs from "../shared/apiHandlers/CustomerAPIs";
import CustomWorkflowQuestionAPIs from "../shared/apiHandlers/CustomWorkflowQuestionAPIs";
import DataAPIs from "../shared/apiHandlers/DataAPIs";
import DomainMappingAPIs from "../shared/apiHandlers/DomainMappingAPIs";
import LocationAPIs from "../shared/apiHandlers/LocationAPIs";
import ResultsAPIs from "../shared/apiHandlers/ResultsAPIs";
import SiteAPIs from "../shared/apiHandlers/SiteAPIs";
import UTP2AccountsAPIs from "../shared/apiHandlers/UTP2AccountsAPIs";
import UTP2FilesAPIs from "../shared/apiHandlers/UTP2FilesAPIs";
import WorkflowApis from "../shared/apiHandlers/WorkflowAPIs";
import WorkOrderApis from "../shared/apiHandlers/WorkOrderAPIs";
import { AuthContext } from "./AuthContext";
import UTP2ReportsAPIs from "../shared/apiHandlers/UTP2ReportsAPIs";
import UTP2HelpAPIs from "../shared/apiHandlers/UTP2HelpAPIs";
import HvSubstationAPIs from "../shared/apiHandlers/HvSubstationAPIs";

interface ApiContextType {
  accessControlApis: AccessControlApis;
  assignmentApis: AssignmentAPIs;
  workOrderApis: WorkOrderApis;
  workflowApis: WorkflowApis;
  assetApis: AssetAPIs;
  customerApis: CustomerAPIs;
  resultsApis: ResultsAPIs;
  utp2FilesAPIs: UTP2FilesAPIs;
  dataApis: DataAPIs;
  addressApis: AddressAPIs;
  locationApis: LocationAPIs;
  customWorkflowQuestionApis: CustomWorkflowQuestionAPIs;
  companyApis: CompanyAPIs;
  siteApis: SiteAPIs;
  accountApis: AccountsApis;
  utp2AccountApis: UTP2AccountsAPIs;
  domainMappingsApis: DomainMappingAPIs;
  utp2ReportsAPIs: UTP2ReportsAPIs;
  utp2HelpApis: UTP2HelpAPIs;
  hvSubstationApis: HvSubstationAPIs;
}

function initialiseApis(onAuthError?: () => void): ApiContextType {
  return {
    accessControlApis: new AccessControlApis({ onAuthError: onAuthError }),
    assignmentApis: new AssignmentAPIs({ onAuthError: onAuthError }),
    workOrderApis: new WorkOrderApis({ onAuthError: onAuthError }),
    workflowApis: new WorkflowApis({ onAuthError: onAuthError }),
    assetApis: new AssetAPIs({ onAuthError: onAuthError }),
    customerApis: new CustomerAPIs({ onAuthError: onAuthError }),
    resultsApis: new ResultsAPIs({ onAuthError: onAuthError }),
    utp2FilesAPIs: new UTP2FilesAPIs({ onAuthError: onAuthError }),
    dataApis: new DataAPIs({ onAuthError: onAuthError }),
    addressApis: new AddressAPIs({ onAuthError: onAuthError }),
    locationApis: new LocationAPIs({ onAuthError: onAuthError }),
    customWorkflowQuestionApis: new CustomWorkflowQuestionAPIs({
      onAuthError: onAuthError,
    }),
    companyApis: new CompanyAPIs({ onAuthError: onAuthError }),
    siteApis: new SiteAPIs({ onAuthError: onAuthError }),
    accountApis: new AccountsApis({ onAuthError: onAuthError }),
    utp2AccountApis: new UTP2AccountsAPIs({ onAuthError: onAuthError }),
    domainMappingsApis: new DomainMappingAPIs({ onAuthError: onAuthError }),
    utp2ReportsAPIs: new UTP2ReportsAPIs({ onAuthError: onAuthError }),
    utp2HelpApis: new UTP2HelpAPIs({ onAuthError: onAuthError }),
    hvSubstationApis: new HvSubstationAPIs({ onAuthError: onAuthError }),
  };
}

/**
 * Context containing initialised API handlers. Use this to get API handlers without having to initialise them in each dependent component.
 *
 * @example
 * // Use in function components
 * const MyFunctionComponent = (props) => {
 *     const apiContext = useContext(ApiContext);
 *     const asset = apiContext.assetApi.getAsset(123);
 * }
 *
 * // Use in class components
 * const MyClassComponent extends React.Component {
 *     static contextType = ApiContext; // Set the component's context to this
 *     context!: React.ContextType<typeof ApiContext>; // Set the context type to help TypeScript
 *
 *     const asset = apiContext.assetApi.getAsset(123);
 * }
 */
export const ApiContext = React.createContext<ApiContextType>({} as ApiContextType);

export const ApiContextProvider = (props: PropsWithChildren<unknown>) => {
  const authContext = useContext(AuthContext);

  return <ApiContext.Provider value={initialiseApis(authContext.logout)}>{props.children}</ApiContext.Provider>;
};
